import { defineMessages, useIntl } from 'react-intl';

import { cfetch } from '@confluence/network';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { Action, type Suggestion } from '../__types__/RemoteObject.ts';

type useSubmitSuggestionActionsProps = {
	spaceKey?: string;
	flags?: FlagsStateContainer;
};

type ActionPayload = {
	id: string;
	title: string;
	description?: string;
	url: string;
	parentId: number;
	action: Action.ACCEPT | Action.REJECT | Action.IGNORE;
};

const i18n = defineMessages({
	successFlagTitle: {
		id: 'space-curation.suggestion-actions.success.title',
		defaultMessage:
			'{count, plural, one {{count} item added to space} other {{count} items added to space}}',
		description: 'The suggestion actions have been submitted successfully',
	},
	successFlagDescription: {
		id: 'space-curation.suggestion-actions.success.description',
		defaultMessage: 'Your selected content has been added to this space.',
		description:
			'Description for the success flag when the suggestion actions have been submitted successfully',
	},
	errorFlagTitle: {
		id: 'space-curation.suggestion-actions.error.title',
		defaultMessage: 'Failed to add items to space',
		description: 'The suggestion actions have not been submitted successfully',
	},
	errorFlagDescription: {
		id: 'space-curation.suggestion-actions.error.description',
		defaultMessage: 'Something went wrong. Please try again.',
		description: 'Description for the error flag when suggestion actions fail to submit',
	},
});

export const useSubmitSuggestionActions = ({
	spaceKey,
	flags,
}: useSubmitSuggestionActionsProps) => {
	const { formatMessage } = useIntl();

	const submitActions = async (
		allSuggestions: Suggestion[],
		selectedIds: Set<string>,
		rejectedIds: Set<string>,
		{ showFlags = true }: { showFlags?: boolean } = {},
	): Promise<boolean> => {
		if (!spaceKey) {
			return false;
		}

		const actions: ActionPayload[] = allSuggestions.map((s) => ({
			...s,
			action: selectedIds.has(s.id)
				? Action.ACCEPT
				: rejectedIds.has(s.id)
					? Action.REJECT
					: Action.IGNORE,
		}));

		try {
			await cfetch(`/wiki/rest/internal/1.0/spaces/${spaceKey}/remote-objects/actions`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ actions }),
			});

			if (showFlags) {
				void flags?.showSuccessFlag({
					id: 'space-navigation.suggestion-actions.success',
					title: formatMessage(i18n.successFlagTitle, { count: selectedIds.size }),
					description: formatMessage(i18n.successFlagDescription),
					isAutoDismiss: true,
				});
			}

			return true;
		} catch {
			if (showFlags) {
				void flags?.showErrorFlag({
					id: 'space-navigation.suggestion-actions.error',
					title: formatMessage(i18n.errorFlagTitle),
					description: formatMessage(i18n.errorFlagDescription),
					isAutoDismiss: true,
				});
			}

			return false;
		}
	};

	return { submitActions };
};
